/*
 * Predictive Search — NuveRise
 * --------------------------------------------------------------
 * Live-search drawer wired to Shopify's /search/suggest.json endpoint.
 * No external deps. Premium wellness brand voice (cream + emerald + ink).
 *
 * Behaviour:
 *   - Trigger button: [data-search-toggle] anywhere in the document
 *   - Cmd+K / Ctrl+K opens from any page
 *   - 180ms input debounce; resources=product (limit 6)
 *   - Keyboard nav: arrows + Enter + Esc, focus trap inside drawer
 *   - aria-live polite count announcer
 *   - prefers-reduced-motion handled in CSS
 *
 * The drawer markup is rendered by snippets/predictive-search.liquid.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-psearch]');
  if (!root) return;

  var input          = root.querySelector('[data-psearch-input]');
  var results        = root.querySelector('[data-psearch-results]');
  var allLink        = root.querySelector('[data-psearch-all]');
  var liveRegion     = root.querySelector('[data-psearch-live]');
  var form           = root.querySelector('[data-psearch-form]');

  if (!input || !results || !allLink || !form) return;

  var POPULAR        = ['Energy', 'Sleep', 'Focus', 'Skin glow', 'Bundles'];
  var DEBOUNCE_MS    = 180;
  var debounceTimer  = null;
  var fetchAbort     = null;
  var selectedIdx    = -1;
  var resultItems    = [];
  var lastTrigger    = null; // restore focus on close
  var isOpen         = false;

  /* ---------- Open / close ---------- */
  function open(triggerEl) {
    if (isOpen) return;
    isOpen = true;
    lastTrigger = triggerEl || document.activeElement;
    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    input.setAttribute('aria-expanded', 'true');
    // Use a microtask to ensure the panel is in the layout before focusing.
    requestAnimationFrame(function () {
      input.focus();
      input.select();
    });
    if (!input.value) renderSuggestions();
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;
    root.hidden = true;
    root.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    input.setAttribute('aria-expanded', 'false');
    input.value = '';
    results.innerHTML = '';
    resultItems = [];
    selectedIdx = -1;
    if (fetchAbort) { try { fetchAbort.abort(); } catch (_) {} fetchAbort = null; }
    if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null; }
    allLink.setAttribute('href', '/search');
    if (lastTrigger && typeof lastTrigger.focus === 'function') {
      lastTrigger.focus();
    }
  }

  /* ---------- Event wiring ---------- */
  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-search-toggle]');
    if (toggle) {
      e.preventDefault();
      if (isOpen) {
        close();
      } else {
        open(toggle);
      }
      return;
    }
    if (e.target.closest('[data-psearch-close]')) {
      e.preventDefault();
      close();
    }
  });

  document.addEventListener('keydown', function (e) {
    // Global Cmd/Ctrl+K
    if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
      e.preventDefault();
      if (isOpen) close(); else open();
      return;
    }
    if (!isOpen) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); move(1); return; }
    if (e.key === 'ArrowUp')   { e.preventDefault(); move(-1); return; }
    if (e.key === 'Enter' && selectedIdx >= 0 && resultItems[selectedIdx]) {
      e.preventDefault();
      window.location.href = resultItems[selectedIdx].getAttribute('data-href');
      return;
    }
    if (e.key === 'Tab') {
      trapFocus(e);
    }
  });

  // Allow form submit (Enter with no result selected) to fall through to /search
  form.addEventListener('submit', function (e) {
    var q = input.value.trim();
    if (!q) { e.preventDefault(); return; }
    // Let the browser navigate to /search?q=...&type=product,page,article
  });

  input.addEventListener('input', function () {
    if (debounceTimer) clearTimeout(debounceTimer);
    var q = input.value.trim();
    allLink.setAttribute('href', '/search?q=' + encodeURIComponent(q));
    if (!q) {
      renderSuggestions();
      announce('Showing popular searches');
      return;
    }
    // Optimistic UI: keep the previous results in place; just tee up the fetch.
    debounceTimer = setTimeout(function () { fetchResults(q); }, DEBOUNCE_MS);
  });

  /* ---------- Fetch ---------- */
  function fetchResults(q) {
    if (fetchAbort) { try { fetchAbort.abort(); } catch (_) {} }
    fetchAbort = (typeof AbortController !== 'undefined') ? new AbortController() : null;

    var url = '/search/suggest.json'
      + '?q=' + encodeURIComponent(q)
      + '&resources[type]=product'
      + '&resources[limit]=6'
      + '&resources[options][unavailable_products]=last';

    var opts = {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    };
    if (fetchAbort) opts.signal = fetchAbort.signal;

    fetch(url, opts)
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        var r = (data && data.resources && data.resources.results) || {};
        var products = r.products || [];
        var pages    = (r.pages || []).concat(r.articles || []);
        if (!products.length && !pages.length) {
          renderEmpty(q);
          announce('No results for ' + q);
          return;
        }
        renderResults(products, pages, q);
        var n = products.length + pages.length;
        announce(n + ' result' + (n === 1 ? '' : 's') + ' for ' + q);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        // Soft-fail: show empty state, never a stack trace.
        renderEmpty(q);
      });
  }

  /* ---------- Rendering ---------- */
  function renderResults(products, pages, q) {
    var html = '';

    if (products.length) {
      html += '<div class="psearch-group" role="group" aria-label="Products">';
      html += '<h3 class="psearch-group-h">Products</h3>';
      html += products.map(productRow).join('');
      html += '</div>';
    }

    if (pages.length) {
      html += '<div class="psearch-group" role="group" aria-label="Journal and pages">';
      html += '<h3 class="psearch-group-h">Journal &amp; pages</h3>';
      html += pages.map(pageRow).join('');
      html += '</div>';
    }

    results.innerHTML = html;
    resultItems = Array.prototype.slice.call(results.querySelectorAll('.psearch-result'));
    resultItems.forEach(function (el, i) {
      el.setAttribute('id', 'psearch-result-' + i);
      el.setAttribute('role', 'option');
      el.setAttribute('aria-selected', 'false');
    });
    selectedIdx = -1;
    results.scrollTop = 0;
  }

  function productRow(p) {
    var img = p.featured_image && p.featured_image.url
            ? p.featured_image.url
            : (p.image || '');
    var thumb = img
      ? '<img src="' + escapeAttr(img) + '" alt="" class="psearch-result__thumb" loading="lazy" width="60" height="60">'
      : '<span class="psearch-result__thumb psearch-result__thumb--placeholder" aria-hidden="true"></span>';

    var priceHtml = '';
    if (p.price != null) {
      priceHtml = '<span class="psearch-result__price">' + escapeHtml(formatPrice(p.price)) + '</span>';
    }

    // Goal tag pill: look at tags array for a "goal:" prefix or just first tag.
    var pill = '';
    if (Array.isArray(p.tags) && p.tags.length) {
      var tag = pickGoalTag(p.tags);
      if (tag) pill = '<span class="psearch-pill">' + escapeHtml(tag) + '</span>';
    }

    return ''
      + '<a href="' + escapeAttr(p.url || '#') + '" class="psearch-result" data-href="' + escapeAttr(p.url || '#') + '">'
      +   thumb
      +   '<div class="psearch-result__info">'
      +     '<p class="psearch-result__title">' + escapeHtml(p.title || 'Untitled') + pill + '</p>'
      +     '<p class="psearch-result__meta">' + priceHtml + '</p>'
      +   '</div>'
      + '</a>';
  }

  function pageRow(p) {
    var summary = p.summary || p.body || p.content || '';
    summary = stripTags(summary).trim();
    if (summary.length > 120) summary = summary.slice(0, 117) + '…';
    if (!summary) summary = (p.object_type === 'article') ? 'View article' : 'View page';

    return ''
      + '<a href="' + escapeAttr(p.url || '#') + '" class="psearch-result" data-href="' + escapeAttr(p.url || '#') + '">'
      +   '<div class="psearch-result__info">'
      +     '<p class="psearch-result__title psearch-result__title--article">' + escapeHtml(p.title || 'Untitled') + '</p>'
      +     '<p class="psearch-result__meta psearch-result__meta--article">' + escapeHtml(summary) + '</p>'
      +   '</div>'
      + '</a>';
  }

  function renderEmpty(q) {
    results.innerHTML = ''
      + '<div class="psearch-empty">'
      +   '<h3>No results for &ldquo;' + escapeHtml(q) + '&rdquo;</h3>'
      +   '<p>Try a broader term, or <a href="/collections/all">browse all products&nbsp;&rarr;</a></p>'
      + '</div>';
    resultItems = [];
    selectedIdx = -1;
  }

  function renderSuggestions() {
    var pills = POPULAR.map(function (p) {
      return '<button type="button" class="psearch-suggest-pill" data-suggest-q="' + escapeAttr(p) + '">' + escapeHtml(p) + '</button>';
    }).join('');
    results.innerHTML = ''
      + '<div class="psearch-suggest">'
      +   '<h3 class="psearch-group-h">Popular searches</h3>'
      +   '<div class="psearch-suggest-pills">' + pills + '</div>'
      + '</div>';
    Array.prototype.forEach.call(results.querySelectorAll('[data-suggest-q]'), function (btn) {
      btn.addEventListener('click', function () {
        input.value = btn.getAttribute('data-suggest-q');
        allLink.setAttribute('href', '/search?q=' + encodeURIComponent(input.value));
        // Trigger the same input pipeline (with no debounce wait needed visually,
        // but we keep the debounce path so behaviour is identical).
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.focus();
      });
    });
    resultItems = [];
    selectedIdx = -1;
  }

  /* ---------- Keyboard nav ---------- */
  function move(dir) {
    if (!resultItems.length) return;
    if (selectedIdx >= 0 && resultItems[selectedIdx]) {
      resultItems[selectedIdx].setAttribute('aria-selected', 'false');
    }
    selectedIdx = (selectedIdx + dir + resultItems.length) % resultItems.length;
    var current = resultItems[selectedIdx];
    current.setAttribute('aria-selected', 'true');
    input.setAttribute('aria-activedescendant', current.id);
    // Smooth scroll into view but only if needed
    if (typeof current.scrollIntoView === 'function') {
      current.scrollIntoView({ block: 'nearest' });
    }
  }

  /* ---------- Focus trap ---------- */
  function trapFocus(e) {
    var focusables = root.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    var first = focusables[0];
    var last  = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ---------- Utilities ---------- */
  function announce(msg) {
    if (!liveRegion) return;
    // Clearing then setting forces SRs to re-announce identical strings.
    liveRegion.textContent = '';
    setTimeout(function () { liveRegion.textContent = msg; }, 20);
  }

  function escapeHtml(s) {
    if (s == null) return '';
    var div = document.createElement('div');
    div.textContent = String(s);
    return div.innerHTML;
  }
  function escapeAttr(s) {
    return escapeHtml(s).replace(/"/g, '&quot;');
  }
  function stripTags(s) {
    if (!s) return '';
    var div = document.createElement('div');
    div.innerHTML = String(s);
    return div.textContent || div.innerText || '';
  }

  function formatPrice(p) {
    // Shopify suggest.json typically returns formatted strings already (e.g. "$48.00").
    if (typeof p === 'string') return p;
    if (typeof p === 'number') {
      // Heuristic: large ints look like cents (e.g. 4800 -> $48.00); small floats like dollars.
      var dollars = (p >= 1000) ? p / 100 : p;
      return '$' + dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return '';
  }

  function pickGoalTag(tags) {
    // Prefer "goal:Energy" → "Energy"; else surface the first non-system tag.
    for (var i = 0; i < tags.length; i++) {
      var t = String(tags[i] || '').trim();
      if (!t) continue;
      if (/^goal:/i.test(t)) return t.replace(/^goal:/i, '').trim();
    }
    return null;
  }
})();
